/**
 * Calendar — every client's obligations on one screen (FR-E7, A-09).
 *
 * `GET /periods/obligations` without a client: every return-capable client's months, most
 * overdue first, filed and locked last, paged. The competitive benchmark lists "a filing
 * calendar across clients" as the practice-app differentiator, and until 2026-10-08 the web
 * never called the route that answers it (gap analysis §2.9).
 *
 * The screen renders and never decides (ADR-0014): the order, the days, the late fee and
 * whether a month is nil all come from the service; `lib/calendar.ts` only turns them into
 * sentences. The late fee's unverified flag and the nil basis ride every row as words, so an
 * assumed figure is never read as a checked one.
 */

import { Alert, Button } from '@fiveox/ui'
import { CalendarDays } from 'lucide-react'
import { useEffect, useState } from 'react'

import { ApiError, type ObligationRecord, periods } from '../lib/api'
import { groupByClient, summarise, summaryWords } from '../lib/calendar'
import { ObligationList } from './ObligationList'

/** Rows per page — a practice of forty clients is 80 rows a month. */
const PAGE = 100

export function Calendar() {
  const [rows, setRows] = useState<ObligationRecord[]>([])
  const [total, setTotal] = useState(0)
  const [asOf, setAsOf] = useState<string | null>(null)
  const [limit, setLimit] = useState(PAGE)
  const [byClient, setByClient] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let live = true
    setLoading(true)
    periods
      .obligations({ limit, offset: 0 })
      .then((body) => {
        if (!live) return
        setRows(body.data)
        setTotal(Number(body.meta?.total ?? body.data.length))
        setAsOf(typeof body.meta?.as_of === 'string' ? body.meta.as_of : null)
        setError(null)
      })
      .catch((err: unknown) => {
        if (!live) return
        setError(err instanceof ApiError ? err.message : 'The calendar could not be loaded.')
      })
      .finally(() => {
        if (live) setLoading(false)
      })
    return () => {
      live = false
    }
  }, [limit])

  const summary = summarise(rows)

  return (
    <div>
      <header className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="gst-h1">
            <CalendarDays size={20} /> Calendar
          </h1>
          <p className="gst-sub">
            Every client&rsquo;s GSTR-1 and GSTR-3B due dates, most overdue first
            {asOf ? ` — as of ${asOf}` : ''}
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          aria-pressed={byClient}
          onClick={() => setByClient((v) => !v)}
        >
          {byClient ? 'Show as one list' : 'Group by client'}
        </Button>
      </header>

      {error && <Alert tone="error" className="mb-4">{error}</Alert>}

      {!loading && !error && rows.length > 0 && (
        <p className="gst-meta mb-4" data-testid="calendar-summary">
          {summaryWords(summary)}
          {total > rows.length ? ` — showing ${rows.length} of ${total} obligations` : ''}
        </p>
      )}

      {loading ? (
        <p className="gst-sub">Loading the calendar…</p>
      ) : byClient ? (
        <div className="space-y-6">
          {groupByClient(rows).map((group) => (
            <section key={group.client_id}>
              <h2 className="gst-panel-h mb-2">{group.client_name}</h2>
              <ObligationList rows={group.rows} showClient={false} />
            </section>
          ))}
        </div>
      ) : (
        <ObligationList rows={rows} showClient />
      )}

      {total > rows.length && !loading && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="mt-3"
          onClick={() => setLimit((n) => n + PAGE)}
        >
          Show {Math.min(PAGE, total - rows.length).toLocaleString('en-IN')} more of{' '}
          {(total - rows.length).toLocaleString('en-IN')}
        </Button>
      )}

      {/* Interest is deliberately not on this screen (D-14): the service answers
          `not_computed` with its gate named, and a column of that would read as a
          column of zeros. It arrives when the computation ships. */}
    </div>
  )
}
